<template>
  <div
    v-if="!isSfAssistantReworkSupported"
    class="sf-pad-t-2 sf-pad-h-3 sf-layer-border-bottom"
  >
    <div
      v-dompurify-html="check.text[currentLocale]?.description"
      class="sf-below-2"
      style="position: relative; overflow: hidden"
    />

    <div v-if="check.attachments" class="sf-below-1">
      <div
        v-for="attachment in check.attachments"
        :key="attachment.id"
        class="sf-label sf-below-1 sf-level-item"
      >
        <div class="sf-level sf-level-small">
          <a
            :href="attachment.url"
            class="sf-a sf-level-item"
            target="_blank"
            >{{ attachment.filename }}</a
          >
        </div>
      </div>
    </div>
    <div class="sf-level sf-level-between sf-below-1">
      <a data-track="sf-assistant-checks-details-toggle" @click="toggleDetails">
        <span v-if="hasDetails">
          {{ detailsText }}
          <span :class="chevronClass"></span>
        </span>
      </a>
      <div v-if="!check.action" class="sf-level sf-level-around">
        <div v-if="!hasRemark">
          <a @click="handleAddTodo">{{ addTodoText }}</a>
        </div>

        <div
          class="sf-level sf-level-around"
          :class="{ 'sf-disabled': completed }"
        >
          <a @click="handleToggleDismissal">
            {{ dismissalText }}
          </a>
        </div>
      </div>

      <div v-if="check.action" class="sf-level sf-level-around">
        <a :data-heap="actionHeapId" @click="handleTriggerAction">
          {{ check.text[currentLocale]?.action }}
        </a>

        <a
          :class="{ 'sf-disabled': completed }"
          :data-heap="dismissalHeapId"
          @click="handleToggleDismissal"
        >
          {{ dismissalText }}
        </a>
      </div>
    </div>

    <div v-if="showDetails" class="sf-pad-v-3">
      <sf-assistant-check-details :details="detailList" />
    </div>

    <remark-card
      v-if="hasRemark"
      :id="remark?.id"
      :key="remark?.id"
      class="sf-assistant-checks__remark"
      :attachment-ids="remark?.attachmentIds"
      :author-id="remark?.authorId"
      :allowed-actions="remark?.allowedActions"
      :bot-name="remark?.botName"
      :comment-ids="remark?.commentIds"
      :company-id="remark?.companyId"
      :context-id="remark?.contextId"
      :context-type="remark?.contextType"
      :created-at="remark?.createdAt"
      :done="remark?.done"
      :done-at="remark?.doneAt"
      :done-by-name="remark?.doneByName"
      :edited-at="remark?.editedAt"
      :edit-title="editTodoTitle"
      :follower-ids="remark?.followerIds"
      :hash-check-diff="hashCheckDiff"
      :hide-period-selector="true"
      :hide-type-selector="true"
      :ledger-archived="remark?.ledgerArchived"
      :ledger-id="remark?.ledgerId"
      :ledger-period-name="remark?.ledgerPeriodName"
      :liquid-input-field-path="remark?.liquidInputFieldPath"
      :rendered-text="remark?.renderedText"
      :show-context="false"
      :sticky="remark?.sticky"
      :tag-ids="remark?.tagIds"
      :text="remark?.text"
      :text-property-value="remark?.textPropertyValue"
      :todo="remark?.todo"
      :updated-at="remark?.updatedAt"
      :variant="remarkCardVariant"
    />
  </div>
  <div
    v-else
    class="
      remark-v2
      tw-flex
      tw-flex-col
      tw-gap-2
      tw-px-6
      tw-py-4
      tw-border
      tw-border-solid
      tw-rounded-lg
      tw-border-gray-300
      tw-group
    "
  >
    <SfMessageContainer :read="check.read">
      <template #heading>
        <div class="tw-flex tw-items-center tw-gap-2">
          <span class="tw-text-sf-ai-dark tw-font-medium tw-text-sm">
            <!-- TODO Translate the following -->
            Assistant
          </span>
          <a
            v-if="check.contextUrl && check.contextUrlText"
            :href="check.contextUrl"
            class="sf-link"
          >
            {{ check.contextUrlText }}
          </a>
        </div>
      </template>
      <template #actions>
        <RemarkActions
          :read="check.read"
          :allowed-actions="{
            openInContext: !!check.contextUrl,
            toggleRead: true,
          }"
          @click="handleRemarkAction"
      /></template>
      <template #body>
        <div
          v-dompurify-html="check.text[currentLocale]?.description"
          class="tw-text-sm"
        ></div>
      </template>
      <template #footer>
        <a
          data-track="sf-assistant-checks-details-toggle"
          class="
            tw-text-xs tw-text-sf-gray
            hover:tw-no-underline
            focus:tw-no-underline
          "
          @click="toggleDetails"
        >
          <div v-if="hasDetails" class="tw-flex tw-items-center tw-gap-2">
            <span :class="chevronClass"></span>
            <span>{{ showDetails ? hideDetailsText : showDetailsText }}</span>
          </div>
        </a>
        <div v-if="showDetails">
          <sf-assistant-check-details :details="detailList" />
        </div>
      </template>
    </SfMessageContainer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import useI18n from '@app/shared/vue/composables/use-I18n'
import SfAssistantCheckDetails from './sf-assistant-check-details.vue'
import RemarkCard from '@app/application/communication/sections/communication/components/remarks/remark-card.vue'
import SfMessageContainer from '@app/shared/vue/components/sf-message/sf-message-container.vue'
import { variants as remarkCardVariants } from '@app/application/communication/sections/communication/components/remarks/constants'
import { Check } from '../types'
import { SfLog } from '@app/adapters/logger'
import { Remark } from '@app/application/communication/types'
import { isFeatureSupported } from '@app/adapters/silverfin'
import RemarkActions from '@app/shared/vue/components/molecules/remark-v2/remark-actions.vue'
import { useChecksStore } from '../stores/checks'
import { RemarkActions as RemarkActionsType } from '@app/shared/vue/components/molecules/remark-v2/types'

interface Props {
  dismissed?: boolean
  completed?: boolean
  check: Check
  remark: Remark // TODO: This should be a fleshed out type, based on remark-card.vue
}

const I18n = useI18n()

const props = withDefaults(defineProps<Props>(), {
  remark: (): Remark => {
    return {} as Remark
  },
})

const emit =
  defineEmits<{
    addTodo: [value: Check]
    toggleDismissal: [value: Check]
    triggerAction: [value: Check]
  }>()

const checksStore = useChecksStore()

const handleAddTodo = (): void => {
  emit('addTodo', props.check)
}

const handleToggleDismissal = (): void => {
  emit('toggleDismissal', props.check)
}

const handleTriggerAction = (): void => {
  emit('triggerAction', props.check)
}

function handleOpenInContext(event: MouseEvent): void {
  const shouldOpenInNewTab = event.metaKey || event.ctrlKey
  if (!props.check.contextUrl) {
    return
  }

  if (shouldOpenInNewTab) {
    window.open(props.check.contextUrl, '_blank')
  } else {
    window.Turbolinks.visit(props.check.contextUrl)
  }
}

function handleRemarkAction(
  action: RemarkActionsType,
  event: MouseEvent
): void {
  switch (action) {
    case 'openInContext':
      handleOpenInContext(event)
      break
    case 'toggleRead':
      checksStore.toggleCheck(props.check.id)
      break
    default:
      SfLog.warn(
        'SfAssistantCheck::handleRemarkAction called with invalid action.',
        {
          extra: {
            action,
          },
        }
      )
  }
}

const isSfAssistantReworkSupported = isFeatureSupported(
  'supports_sf_assistant_rework'
)

const showDetails = ref(false)

// Translations
const detailsText = I18n.t('account_entry.assistant.check.details')
const showDetailsText = I18n.t('account_entry.assistant.check.show_details')
const hideDetailsText = I18n.t('account_entry.assistant.check.hide_details')
const dismissalText = I18n.t(
  `account_entry.assistant.check.${props.dismissed ? 'undismiss' : 'dismiss'}`
)
const addTodoText = I18n.t('account_entry.assistant.check.add_todo')
const editTodoTitle = I18n.t('account_entry.assistant.check.edit_todo_title')

// computed
const chevronClass = computed(() => {
  return `icon-chevron-${showDetails.value ? 'up' : 'down'}`
})

type availableLocales = keyof typeof props.check.text
const currentLocale = computed((): availableLocales => {
  if (I18n.locale in props.check.text) {
    return I18n.locale
  } else if (I18n.defaultLocale in props.check.text) {
    return I18n.defaultLocale
  } else {
    return (Object.keys(props.check.text)[0] as availableLocales) || 'en'
  }
})

const detailList = computed(() => {
  const detailList = props.check.text[currentLocale.value]?.detail?.details
  return detailList || []
})

const hasDetails = computed(() => {
  return detailList.value && detailList.value.length > 0
})

const hasRemark = computed(() => {
  return props.remark && Object.keys(props.remark).length > 0
})

const remarkCardVariant = computed(() => {
  return remarkCardVariants.inlined
})

function toggleDetails(): void {
  showDetails.value = !showDetails.value
}

const hashCheckDiff = computed((): boolean => {
  if (props.remark?.assistantCheckHash && props.check.hash) {
    return props.check.hash !== props.remark.assistantCheckHash
  }

  return false
})

const actionHeapIdPrefix = 'assistant-action-execute-'
const actionOpenConfirmationHeapIdPrefix = 'assistant-action-open-confirm-'
const dismissalHeapIdPrefix = 'assistant-action-dismiss-'
const undismissalHeapIdPrefix = 'assistant-action-undismiss-'

const hyphenatedActionType = computed((): string | undefined => {
  return props.check.action?.actionType.replace(/_/g, '-')
})

const actionHeapId = computed((): string => {
  // This is a shortcut solution to assign a custom Heap id to the action button
  // that doesn't trigger an action but opens a confirmation dialog.
  if (props.check.action?.actionType === 'change_account_detail_template') {
    return `${actionOpenConfirmationHeapIdPrefix}${hyphenatedActionType.value}`
  }

  return `${actionHeapIdPrefix}${hyphenatedActionType.value}`
})

const dismissalHeapId = computed((): string | null => {
  return `${props.dismissed ? undismissalHeapIdPrefix : dismissalHeapIdPrefix}${
    hyphenatedActionType.value
  }`
})
</script>
